/**
 * @file useBlendDraft.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-08-25
 * @description The blend being built: inclusion values, locked rows, and
 *              undo/redo over both.
 *
 *              History is pushed on COMMITS, not keystrokes — typing "22.5"
 *              should be one undo step, not four. Consecutive edits to the same
 *              cell inside COALESCE_MS collapse into one; anything discrete
 *              (paste, add, remove, lock, balance) always starts a new step.
 *
 *              Values are held as the user typed them (strings), not coerced to
 *              numbers on every keystroke — otherwise "12." collapses to 12 the
 *              moment the decimal point is typed and the next digit lands in the
 *              wrong place. blendMath.toNumber does the coercion when computing.
 */

import { useCallback, useMemo, useReducer } from 'react';
import * as blendMath from '../utils/blendMath';

const HISTORY_LIMIT = 50;
const COALESCE_MS = 600;

const emptyState = (values = {}, locked = []) => ({
  past: [],
  present: { values, locked },
  future: [],
  lastKey: null,
  lastAt: 0
});

function pushPast(state, present) {
  const past = [...state.past, state.present];
  return past.length > HISTORY_LIMIT ? past.slice(past.length - HISTORY_LIMIT) : past;
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET': {
      const { key, value, at } = action;
      // Same cell, still typing → fold into the previous step.
      const coalesce = state.lastKey === key && (at - state.lastAt) < COALESCE_MS;
      return {
        past: coalesce ? state.past : pushPast(state),
        present: {
          ...state.present,
          values: { ...state.present.values, [key]: value }
        },
        future: [],
        lastKey: key,
        lastAt: at
      };
    }

    // Discrete edits: always their own undo step.
    case 'REPLACE':
      return {
        past: pushPast(state),
        present: {
          values: action.values ?? state.present.values,
          locked: action.locked ?? state.present.locked
        },
        future: [],
        lastKey: null,
        lastAt: 0
      };

    case 'UNDO': {
      if (!state.past.length) return state;
      const previous = state.past[state.past.length - 1];
      return {
        past: state.past.slice(0, -1),
        present: previous,
        future: [state.present, ...state.future],
        lastKey: null,
        lastAt: 0
      };
    }

    case 'REDO': {
      if (!state.future.length) return state;
      const [next, ...rest] = state.future;
      return {
        past: [...state.past, state.present],
        present: next,
        future: rest,
        lastKey: null,
        lastAt: 0
      };
    }

    // Loading a saved blend replaces everything and clears history — undoing
    // back into a different blend's recipe would be nonsense.
    case 'LOAD':
      return emptyState(action.values || {}, action.locked || []);

    default:
      return state;
  }
}

export default function useBlendDraft(initialValues = {}, initialLocked = []) {
  const [state, dispatch] = useReducer(
    reducer, undefined, () => emptyState(initialValues, initialLocked)
  );

  const values = state.present.values;
  const locked = state.present.locked;

  const setValue = useCallback((key, value) => {
    dispatch({ type: 'SET', key, value, at: Date.now() });
  }, []);

  const addMaterial = useCallback((key, value = '') => {
    dispatch({ type: 'REPLACE', values: { ...values, [key]: value } });
  }, [values]);

  const removeMaterial = useCallback((key) => {
    const next = { ...values };
    delete next[key];
    dispatch({ type: 'REPLACE', values: next, locked: locked.filter((k) => k !== key) });
  }, [values, locked]);

  const toggleLock = useCallback((key) => {
    dispatch({
      type: 'REPLACE',
      locked: locked.includes(key) ? locked.filter((k) => k !== key) : [...locked, key]
    });
  }, [locked]);

  /** Bulk write, from a paste or from loading a version into the current draft. */
  const replaceValues = useCallback((nextValues, nextLocked) => {
    dispatch({ type: 'REPLACE', values: nextValues, locked: nextLocked });
  }, []);

  /** Distribute the shortfall to 100 across the unlocked rows. */
  /**
   * Fill or trim to 100%, using only the unlocked materials.
   *
   * @param {string} [mode='proportional'] how they give way — proportional
   *   keeps their ratios, equal moves them all by the same amount.
   */
  const balance = useCallback((mode = 'proportional') => {
    dispatch({ type: 'REPLACE', values: blendMath.balance(values, locked, 2, mode) });
  }, [values, locked]);

  /** Set one cell to whatever is missing from 100 — the `=` shortcut. */
  const fillToBalance = useCallback((key) => {
    const others = { ...values };
    delete others[key];
    const remaining = Math.round((100 - blendMath.total(others)) * 100) / 100;
    dispatch({ type: 'REPLACE', values: { ...values, [key]: remaining } });
  }, [values]);

  const load = useCallback((nextValues, nextLocked) => {
    dispatch({ type: 'LOAD', values: nextValues, locked: nextLocked });
  }, []);

  const clear = useCallback(() => dispatch({ type: 'LOAD', values: {}, locked: [] }), []);

  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), []);

  return useMemo(() => ({
    values,
    locked,
    setValue,
    addMaterial,
    removeMaterial,
    toggleLock,
    replaceValues,
    balance,
    fillToBalance,
    load,
    clear,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0
  }), [values, locked, setValue, addMaterial, removeMaterial, toggleLock, replaceValues,
       balance, fillToBalance, load, clear, undo, redo, state.past.length, state.future.length]);
}
